<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import BetaAppearanceNavigation from '@/components/beta-dashboard/organisms/BetaAppearanceNavigation.vue'
import BetaApplicationLogo from '@/components/beta-dashboard/atoms/BetaApplicationLogo.vue'
import BetaMobileQuickActionsMenu from '@/components/beta-dashboard/organisms/BetaMobileQuickActionsMenu.vue'
import BetaPagesNavigation from '@/components/beta-dashboard/organisms/BetaPagesNavigation.vue'
import BetaProfileMenu from '@/components/beta-dashboard/molecules/BetaProfileMenu.vue'
import BetaSettingsNavigation from '@/components/beta-dashboard/organisms/BetaSettingsNavigation.vue'
import { useBetaPanelNavigation } from '@/components/beta-dashboard/useBetaPanelNavigation'
import { useApplicationStore } from '@/stores/application'
import { useSiteStore } from '@/stores/site'
import { useUserStore } from '@/stores/user'
import type {
    BetaCreatePageKind,
    BetaDashboardView,
    BetaPage,
    BetaSettingsSection,
} from '@/types/beta-dashboard'
import type { BetaAdvancedSettingsRouteName } from '@/utils/beta-settings-navigation'

const props = defineProps<{
    activeView: BetaDashboardView
    storeLoading?: boolean
    pages: BetaPage[]
    selectedPageId?: number | null
    unavailableKinds?: BetaCreatePageKind[]
    creatingPost?: boolean
    loggingOut?: boolean
    settingsSection: BetaSettingsSection
}>()

const emit = defineEmits<{
    navigate: [view: BetaDashboardView]
    openStore: []
    selectPage: [page: BetaPage]
    configurePage: [page: BetaPage]
    createPage: [kind?: BetaCreatePageKind]
    editHome: []
    createPost: [pageId: number]
    openSettings: [section: BetaSettingsSection]
    openAdvancedSettings: [routeName: BetaAdvancedSettingsRouteName]
    logout: []
}>()

const router = useRouter()
const applicationStore = useApplicationStore()
const siteStore = useSiteStore()
const userStore = useUserStore()

const mobileMenuOpen = ref(false)
const { navItems, openingDesign, openDesign } = useBetaPanelNavigation()

const bottomNavOrder = ['home', 'content', 'opportunities', 'settings'] as const
const bottomNavItems = computed(() => bottomNavOrder.map((id) => ({
    ...navItems.value.find((item) => item.id === id)!,
    id,
})))

const siteUrl = computed(() => {
    if (!siteStore.url) return null

    return /^https?:\/\//.test(String(siteStore.url))
        ? String(siteStore.url)
        : `https://${siteStore.url}`
})

const navigate = (view: BetaDashboardView) => {
    mobileMenuOpen.value = false
    emit('navigate', view)
}

const openDesignFromMenu = () => {
    mobileMenuOpen.value = false
    void openDesign()
}

const selectPage = (page: BetaPage) => {
    mobileMenuOpen.value = false
    emit('selectPage', page)
}

const configurePage = (page: BetaPage) => {
    mobileMenuOpen.value = false
    emit('configurePage', page)
}

const openSite = () => {
    if (!siteUrl.value) return

    window.open(siteUrl.value, '_blank', 'noopener,noreferrer')
}

const openLegacy = () => {
    router.push({ name: 'dashboard' })
}

const editHome = () => {
    mobileMenuOpen.value = false
    emit('editHome')
}

const createPage = () => {
    mobileMenuOpen.value = false
    emit('createPage')
}

const createPost = (pageId: number) => {
    mobileMenuOpen.value = false
    emit('createPost', pageId)
}

const previewSite = () => {
    mobileMenuOpen.value = false
    openSite()
}

const openSettings = (section: BetaSettingsSection) => {
    mobileMenuOpen.value = false
    emit('openSettings', section)
}

const openAdvancedSettings = (routeName: BetaAdvancedSettingsRouteName) => {
    mobileMenuOpen.value = false
    emit('openAdvancedSettings', routeName)
}
</script>

<template>
    <div class="beta-dashboard-shell">
        <aside class="beta-dashboard-sidebar">
            <nav aria-label="Navegação principal" class="beta-dashboard-nav">
                <template
                    v-for="item in navItems"
                    :key="item.id"
                >
                    <BetaPagesNavigation
                        v-if="item.id === 'content'"
                        :pages="props.pages"
                        :active="props.activeView === 'content'"
                        :selected-page-id="props.selectedPageId"
                        @browse="navigate('content')"
                        @select="selectPage"
                        @configure="configurePage"
                        @create="createPage"
                    />
                    <BetaAppearanceNavigation
                        v-else-if="item.id === 'appearance'"
                        :active="props.activeView === 'layout'"
                        :opening-design="openingDesign"
                        @design="openDesignFromMenu"
                        @layout="navigate('layout')"
                    />
                    <BetaSettingsNavigation
                        v-else-if="item.id === 'settings'"
                        :active="props.activeView === 'settings'"
                        :section="props.settingsSection"
                        @select="openSettings"
                        @advanced="openAdvancedSettings"
                    />
                    <button
                        v-else
                        type="button"
                        :aria-current="props.activeView === item.id ? 'page' : undefined"
                        :class="[
                            'beta-dashboard-nav__item',
                            props.activeView === item.id && 'is-active',
                        ]"
                        @click="navigate(item.id)"
                    >
                        <component :is="item.icon" class="beta-dashboard-nav__icon" />
                        <span>
                            <strong>
                                {{ item.label }}
                            </strong>
                        </span>
                    </button>
                </template>
            </nav>

            <div class="beta-dashboard-sidebar__footer">
                <button type="button" class="beta-dashboard-store" @click="emit('openStore')">
                    <span class="beta-dashboard-store__icon">↗</span>
                    <span>
                        <strong>{{ props.storeLoading ? 'Abrindo loja…' : 'Acessar loja' }}</strong>
                    </span>
                </button>

                <button type="button" class="beta-dashboard-legacy" @click="openLegacy">
                    <svg class="beta-dashboard-nav__icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M4.63552 3.20005L1.59961 6.27704M1.59961 6.27704L4.63552 9.35404M1.59961 6.27704L11.1381 6.27704C12.9394 6.27704 14.3996 7.73727 14.3996 9.53855C14.3996 11.3398 12.9394 12.8 11.1381 12.8L7.99961 12.8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span>
                        <strong>Voltar ao painel clássico</strong>
                    </span>
                </button>
            </div>
        </aside>

        <div class="beta-dashboard-main">
            <header class="beta-dashboard-topbar">
                <button
                    type="button"
                    class="beta-dashboard-mobile-menu"
                    :aria-expanded="mobileMenuOpen"
                    :aria-label="mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'"
                    aria-controls="beta-dashboard-mobile-navigation"
                    @click="mobileMenuOpen = !mobileMenuOpen"
                >
                    <span aria-hidden="true">☰</span>
                    <span class="sr-only">{{ mobileMenuOpen ? 'Fechar menu' : 'Abrir menu' }}</span>
                </button>

                <BetaApplicationLogo
                    class="beta-dashboard-topbar__brand"
                    :src="applicationStore.appLogo"
                    :name="applicationStore.appName"
                />

                <div class="beta-dashboard-topbar__actions">
                    <button
                        type="button"
                        class="beta-dashboard-preview-button"
                        :disabled="!siteUrl"
                        @click="openSite"
                    >
                        <span>Visualizar site</span>
                        <span aria-hidden="true">↗</span>
                    </button>

                    <BetaProfileMenu
                        :avatar="siteStore.avatar"
                        :name="userStore.name"
                        :email="userStore.email"
                        :profile-url="applicationStore.panelUrl"
                        :logging-out="props.loggingOut"
                        @logout="emit('logout')"
                    />
                </div>
            </header>

            <div
                v-if="mobileMenuOpen"
                id="beta-dashboard-mobile-navigation"
                class="beta-dashboard-mobile-nav"
            >
                <template
                    v-for="item in navItems"
                    :key="item.id"
                >
                    <BetaPagesNavigation
                        v-if="item.id === 'content'"
                        :pages="props.pages"
                        :active="props.activeView === 'content'"
                        :selected-page-id="props.selectedPageId"
                        @browse="navigate('content')"
                        @select="selectPage"
                        @configure="configurePage"
                        @create="createPage"
                    />
                    <BetaAppearanceNavigation
                        v-else-if="item.id === 'appearance'"
                        :active="props.activeView === 'layout'"
                        :opening-design="openingDesign"
                        @design="openDesignFromMenu"
                        @layout="navigate('layout')"
                    />
                    <BetaSettingsNavigation
                        v-else-if="item.id === 'settings'"
                        :active="props.activeView === 'settings'"
                        :section="props.settingsSection"
                        @select="openSettings"
                        @advanced="openAdvancedSettings"
                    />
                    <button
                        v-else
                        type="button"
                        :class="[
                            'beta-dashboard-mobile-nav__item',
                            props.activeView === item.id && 'is-active',
                        ]"
                        @click="navigate(item.id)"
                    >
                        <component :is="item.icon" class="beta-dashboard-nav__icon" />
                        {{ item.label }}
                    </button>
                </template>
                <button type="button" class="beta-dashboard-mobile-nav__item" @click="openLegacy">
                    <svg class="beta-dashboard-nav__icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M4.63552 3.20005L1.59961 6.27704M1.59961 6.27704L4.63552 9.35404M1.59961 6.27704L11.1381 6.27704C12.9394 6.27704 14.3996 7.73727 14.3996 9.53855C14.3996 11.3398 12.9394 12.8 11.1381 12.8L7.99961 12.8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    Voltar ao painel clássico
                </button>
            </div>

            <main
                :class="[
                    'beta-dashboard-content',
                    props.activeView === 'content' && 'is-wide',
                ]"
            >
                <slot />
            </main>
        </div>

        <nav
            aria-label="Navegação mobile"
            class="beta-dashboard-bottom-nav"
        >
            <button
                v-for="item in bottomNavItems.slice(0, 2)"
                :key="item.id"
                type="button"
                :aria-current="props.activeView === item.id ? 'page' : undefined"
                :class="[
                    'beta-dashboard-bottom-nav__item',
                    props.activeView === item.id && 'is-active',
                ]"
                @click="navigate(item.id)"
            >
                <component :is="item.icon" class="beta-dashboard-bottom-nav__icon" />
                <span>{{ item.label }}</span>
            </button>

            <BetaMobileQuickActionsMenu
                class="beta-dashboard-bottom-nav__quick-actions"
                :pages="props.pages"
                :can-preview="Boolean(siteUrl)"
                :creating-post="props.creatingPost"
                @edit-home="editHome"
                @create-page="createPage"
                @create-post="createPost"
                @preview="previewSite"
            />

            <button
                v-for="item in bottomNavItems.slice(2)"
                :key="item.id"
                type="button"
                :aria-current="props.activeView === item.id ? 'page' : undefined"
                :class="[
                    'beta-dashboard-bottom-nav__item',
                    props.activeView === item.id && 'is-active',
                ]"
                @click="navigate(item.id)"
            >
                <component :is="item.icon" class="beta-dashboard-bottom-nav__icon" />
                <span>{{ item.label }}</span>
            </button>
        </nav>
    </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=Outfit:wght@500;600;700&display=swap');

.beta-dashboard-shell {
    --beta-accent: var(--main-color, #2f86f6);
    min-height: 100vh;
    background: #f8fafc;
    color: #172132;
    font-family: 'Figtree', ui-sans-serif, system-ui, sans-serif;
}

.beta-dashboard-sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 30;
    display: flex;
    width: 255px;
    flex-direction: column;
    border-right: 1px solid #e3e9f0;
    background: #f3f7fb;
    padding: 16px;
}

.beta-dashboard-nav {
    display: grid;
    min-height: 0;
    flex: 1;
    align-content: start;
    gap: 4px;
    overflow-y: auto;
    padding-right: 2px;
}

.beta-dashboard-nav__item,
.beta-dashboard-store,
.beta-dashboard-legacy {
    display: grid;
    width: 100%;
    grid-template-columns: 34px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
    border-radius: 17px;
    padding: 9px 12px;
    text-align: left;
    transition: 160ms ease;
}

.beta-dashboard-nav__item {
    min-height: 54px;
    color: #172132;
}

.beta-dashboard-nav__item:hover,
.beta-dashboard-nav__item.is-active {
    background: color-mix(in srgb, var(--beta-accent) 12%, white);
    color: var(--beta-accent);
}

.beta-dashboard-nav__item:disabled {
    cursor: wait;
    opacity: 0.58;
}

.beta-dashboard-nav__item > :deep(svg) {
    box-sizing: content-box;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #edf1f6;
    padding: 9px;
    color: #7b8797;
}

.beta-dashboard-nav__item.is-active > :deep(svg) {
    background: color-mix(in srgb, var(--beta-accent) 18%, white);
    color: var(--beta-accent);
}

.beta-dashboard-nav__icon {
    width: 17px;
    height: 17px;
}

.beta-dashboard-nav__item strong,
.beta-dashboard-store strong,
.beta-dashboard-legacy strong {
    display: block;
    font-size: 13px;
    font-weight: 500;
}

.beta-dashboard-sidebar__footer {
    margin-top: auto;
    display: grid;
    gap: 8px;
}

.beta-dashboard-store {
    min-height: 62px;
    border: 1px solid #e0e6ed;
    background: #fff;
    color: #172132;
}

.beta-dashboard-store__icon {
    display: grid;
    width: 30px;
    height: 30px;
    place-items: center;
    border-radius: 50%;
    background: #edf1f6;
    color: #8792a1;
}

.beta-dashboard-legacy {
    min-height: 58px;
    border: 1px solid #e0e6ed;
    background: #fff;
    color: #172132;
}

.beta-dashboard-legacy:hover {
    border-color: #cfd8e4;
    background: #f9fbfd;
}

.beta-dashboard-legacy .beta-dashboard-nav__icon {
    box-sizing: content-box;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #edf1f6;
    padding: 7px;
    color: #8792a1;
}

.beta-dashboard-main {
    min-height: 100vh;
    padding-left: 255px;
}

.beta-dashboard-topbar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    min-height: 68px;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #e3e9f0;
    background: rgba(248, 250, 252, 0.96);
    padding: 10px 40px;
    backdrop-filter: blur(12px);
}

.beta-dashboard-topbar__brand {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
}

.beta-dashboard-topbar__actions {
    display: flex;
    margin-left: auto;
    align-items: center;
    gap: 12px;
}

.beta-dashboard-preview-button {
    display: flex;
    min-height: 35px;
    align-items: center;
    gap: 10px;
    border: 1px solid #dfe5ec;
    border-radius: 999px;
    background: #fff;
    padding: 0 15px;
    color: #172132;
    font-size: 11px;
    font-weight: 500;
}

.beta-dashboard-preview-button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.beta-dashboard-mobile-menu,
.beta-dashboard-mobile-nav,
.beta-dashboard-bottom-nav {
    display: none;
}

.beta-dashboard-content {
    margin: 0 auto;
    width: min(calc(100% - 48px), 1072px);
    padding: 28px 0 52px;
}

.beta-dashboard-content.is-wide {
    width: min(calc(100% - clamp(48px, 5vw, 96px)), 1920px);
}

@media (max-width: 1023px) {
    .beta-dashboard-sidebar {
        display: none;
    }

    .beta-dashboard-main {
        padding-left: 0;
    }

    .beta-dashboard-mobile-menu {
        display: grid;
        width: 42px;
        height: 42px;
        place-items: center;
        border: 1px solid #dce5f2;
        border-radius: 14px;
        background: #fff;
        color: #29405f;
        font-size: 18px;
    }

    .beta-dashboard-mobile-nav {
        position: fixed;
        inset: 76px 16px auto 16px;
        z-index: 50;
        display: grid;
        max-height: calc(100vh - 92px);
        gap: 8px;
        border: 1px solid #e1e8f2;
        border-radius: 20px;
        background: #fff;
        padding: 12px;
        overflow-y: auto;
    }

    .beta-dashboard-mobile-nav__item {
        display: flex;
        align-items: center;
        gap: 12px;
        border-radius: 14px;
        padding: 12px;
        color: #60718a;
        font-size: 13px;
        font-weight: 800;
    }

    .beta-dashboard-mobile-nav__item.is-active {
        background: color-mix(in srgb, var(--beta-accent) 10%, white);
        color: var(--beta-accent);
    }

    .beta-dashboard-mobile-nav__item:disabled {
        cursor: wait;
        opacity: 0.58;
    }
}

@media (max-width: 767px) {
    .beta-dashboard-topbar {
        min-height: 68px;
        padding: 10px 16px;
    }

    .beta-dashboard-topbar__brand {
        max-width: 82px;
    }

    .beta-dashboard-preview-button {
        width: 40px;
        height: 40px;
        min-height: 40px;
        justify-content: center;
        gap: 0;
        border-radius: 50%;
        padding: 0;
    }

    .beta-dashboard-preview-button span:first-child {
        display: none;
    }

    .beta-dashboard-content {
        width: auto;
        padding: 20px 16px 100px;
    }

    .beta-dashboard-content.is-wide {
        width: auto;
    }

    .beta-dashboard-bottom-nav {
        position: fixed;
        inset: auto 0 0 0;
        z-index: 40;
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        border-top: 1px solid #e1e8f2;
        background: rgba(255, 255, 255, 0.96);
        padding: 8px max(8px, env(safe-area-inset-right)) calc(8px + env(safe-area-inset-bottom)) max(8px, env(safe-area-inset-left));
        backdrop-filter: blur(18px);
    }

    .beta-dashboard-bottom-nav :deep(.beta-dashboard-bottom-nav__quick-actions) {
        grid-column: 3;
        width: 100%;
        justify-self: stretch;
    }

    .beta-dashboard-bottom-nav__item {
        display: grid;
        justify-items: center;
        gap: 4px;
        border-radius: 12px;
        padding: 8px;
        color: #7a8ba4;
        font-size: 10px;
        font-weight: 800;
    }

    .beta-dashboard-bottom-nav__item.is-active {
        color: var(--beta-accent);
    }

    .beta-dashboard-bottom-nav__icon {
        width: 19px;
        height: 19px;
    }
}
</style>
